// ============================================
// HLX-PRO.01 — Input Assembly & Profile Merge Types
//
// First step of the Protocol pipeline.
// Merges CaptainResponse + SafetyDataProfile →
// single validated RawProtocolInput.
//
// Silver MVP scope:
// 1. RawProtocolInput (D-1)
// 2. InputAssemblyError (D-2)
// 3. ConservativeDefaults (D-3)
// 4. DataCompleteness (D-3)
// 5. SafetyDataProfile (input contract)
//
// LOCKED CONSTRAINTS:
// - Conservative Default Policy (FD-2)
// - Sex unknown → block gender-specific products
// - Pregnancy unknown (female/unknown) → block Cat X
// - Allergies unknown → block common allergen products
// - Empty array + skipped modal ≠ confirmed empty
// ============================================

import type { CaptainBiomarkerResult } from './captainPipeline';

// ── Assembly Trigger ──

export type AssemblyTrigger =
  | 'INITIAL_UPLOAD'       // First lab upload
  | 'CAPTAIN_COMPLETE'     // Captain pipeline complete
  | 'CHAT_DISCLOSURE'      // Chat-based disclosure (C12)
  | 'MODAL_UPDATE'         // Safety modal update
  | 'LATE_REVEAL'          // PRO.09 late reveal
  | 'SESSION_RESUME'       // PRO.11 session resume
  | 'MANUAL_REASSEMBLY';   // Manual trigger

// ── Data Completeness ──

export interface DataCompleteness {
  /** Patient explicitly confirmed sex (not inferred/skipped) */
  sexProvided: boolean;
  /** Patient explicitly confirmed age */
  ageProvided: boolean;
  /** Patient explicitly answered pregnancy question */
  pregnancyStatusProvided: boolean;
  /** Patient provided medications (even if empty = "I take none") */
  medicationsProvided: boolean;
  /** Patient provided allergies (even if empty = "No allergies") */
  allergiesProvided: boolean;
  /** Patient provided conditions (even if empty = "No conditions") */
  conditionsProvided: boolean;
}

// ── Conservative Defaults ──

export interface ConservativeDefaults {
  /** Sex unknown → blocks gender-specific products */
  sexUnknown: boolean;
  /** Pregnancy unknown (female or sex unknown) → blocks Category X */
  pregnancyUnknown: boolean;
  /** Medications unknown → Gate 2 escalation via PRO.08 */
  medicationsUnknown: boolean;
  /** Allergies unknown → blocks common allergen products */
  allergiesUnknown: boolean;
  /** Whether ANY conservative defaults are active */
  anyDefaultsActive: boolean;
}

// ── Assembly Sources ──

export interface AssemblySources {
  /** How many data points came from modals */
  modalSourceCount: number;
  /** How many data points came from chat disclosures */
  chatSourceCount: number;
  /** How many data points came from triage/intake */
  triageSourceCount: number;
}

// ── Assembly Validation ──

export interface AssemblyValidation {
  /** CaptainResponse extraction status verified */
  extractionStatusVerified: boolean;
  /** Biomarker array is non-empty */
  biomarkerArrayNonEmpty: boolean;
  /** Safety profile is present */
  safetyProfilePresent: boolean;
  /** Assembly completed successfully */
  assemblySuccessful: boolean;
}

// ── Safety Data Profile (PRO.01 input contract) ──

export interface SafetyDataProfile {
  /** Patient sex — undefined if not provided */
  sex?: 'male' | 'female';
  /** Patient age — undefined if not provided */
  age?: number;
  /** Pregnancy status — undefined if not answered */
  isPregnant?: boolean;
  /** Breastfeeding status — undefined if not answered */
  isBreastfeeding?: boolean;
  /** Medications from modals */
  modalMedications: string[];
  /** Medications disclosed in chat */
  chatMedications: string[];
  /** Allergies from modals */
  modalAllergies: string[];
  /** Allergies disclosed in chat */
  chatAllergies: string[];
  /** Conditions from modals */
  modalConditions: string[];
  /** Conditions disclosed in chat */
  chatConditions: string[];
  /** Whether medication modal was completed (not skipped) */
  medicationsProvided: boolean;
  /** Whether allergy modal was completed (not skipped) */
  allergiesProvided: boolean;
  /** Whether conditions modal was completed (not skipped) */
  conditionsProvided: boolean;
  /** Whether sex was explicitly provided */
  sexProvided: boolean;
  /** Whether age was explicitly provided */
  ageProvided: boolean;
  /** Whether pregnancy question was explicitly answered */
  pregnancyStatusProvided: boolean;
  /** ISO 8601 timestamp of last update */
  lastUpdated: string;
}

// ── Raw Protocol Input (PRO.01 output → PRO.02+ input) ──

export interface RawProtocolInput {
  // Identity
  sessionId: string;
  patientId: string;
  assembledAt: string;  // ISO 8601
  assemblyVersion: string;
  assemblyTrigger: AssemblyTrigger;

  // Biomarkers (from CaptainResponse)
  biomarkers: CaptainBiomarkerResult[];

  // Safety (merged from SafetyDataProfile)
  sex: 'male' | 'female' | 'unknown';
  age: number | null;
  isPregnant: boolean | null;
  isBreastfeeding: boolean | null;
  medications: string[];
  /** Normalized medication RxCUI IDs for contraindication checking (R2 — PRO.02) */
  medicationRxCuis?: Record<string, string>;  // Maps canonical name → RxCUI
  allergies: string[];
  conditions: string[];

  // Completeness & Defaults
  dataCompleteness: DataCompleteness;
  conservativeDefaults: ConservativeDefaults;

  // Provenance
  sources: AssemblySources & {
    /** Whether late reveal was applied during this assembly */
    lateRevealApplied?: boolean;
    /** Whether conservative defaults were applied */
    conservativeDefaultsApplied?: boolean;
  };
  validation: AssemblyValidation;

  // Extraction traceability (PRO.01 #5 red team)
  /** Captain extraction session ID for traceability */
  extractionSessionId?: string;
  /** Captain extraction timestamp for traceability */
  extractionTimestamp?: string;
}

// ── Input Assembly Error ──

export type InputAssemblyErrorCode =
  | 'EXTRACTION_INCOMPLETE'  // Critical biomarkers missing
  | 'EXTRACTION_FAILED'      // CaptainResponse.success === false
  | 'BIOMARKERS_EMPTY'       // Zero biomarkers extracted
  | 'SAFETY_PROFILE_MISSING' // SafetyDataProfile is null/undefined
  | 'SAFETY_PROFILE_STALE'   // SafetyDataProfile too old
  | 'SESSION_MISMATCH'       // Session IDs don't match
  | 'ASSEMBLY_INTERNAL';     // Unexpected internal error

// ── Category X Products (pregnancy conservative default) ──

export const CATEGORY_X_PRODUCT_KEYWORDS: string[] = [
  'tretinoin',
  'finasteride',
  'rapamycin',
  'sirolimus',
  'methotrexate',
  'high-dose vitamin a',
  'isotretinoin',
];

// ── Common Allergen Product Keywords ──

export const COMMON_ALLERGEN_KEYWORDS: string[] = [
  'fish oil',
  'omega-3',
  'peanut',
  'soy',
  'dairy',
  'whey',
  'casein',
  'bee pollen',
  'wheat grass',
  'shellfish',
  'egg',
];

// ── Assembly Version ──

export const ASSEMBLY_VERSION = '1.0.0';

// ── Safety Profile Staleness ──

export const SAFETY_PROFILE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
